<template>
  <div class="ucrs-gallery">
    <div class="ucrs-gallery__inner">
      <div
        v-if="media && property.features.virtual_tour"
        v-show="showVirtualTour"
        class="ucrs-gallery__mediaContainer"
      >
        <iframe
          width="100%"
          allowfullscreen
          height="100%"
          border="0"
          frameborder="0"
          allow="xr-spatial-tracking"
          xr-environment
          :src="virtualTourSrc"
          loading="lazy"
        />
      </div>
      <div
        v-if="renderYouTube && media && property.features.url_video"
        v-show="showVideo"
        class="ucrs-gallery__mediaContainer"
      >
        <LazyYoutubeVideo
          class="w-full h-full"
          autoplay
          :src="`https://www.youtube.com/embed/${videoId}`"
          :parameters="{ controls: 0 }"
        />
      </div>
      <ClientOnly>
        <LazySwiper
          v-if="property.images && property.images.length"
          :options="swiperOptions"
          navigation
          pagination
          virtual
          nav-position="bottom-center"
          @slide-index="updateCurrentSlide"
        >
          <div
            v-for="(img, index) in property.images"
            :key="index"
            class="swiper-slide ucrs-gallery__overlay cursor-pointer bg-cover"
            :style="swiperBackground"
            @click="clickImage(index)"
          >
            <NuxtImg
              class="ucrs-gallery__item"
              :src="$utilities.imgAlias(img.src)"
              :alt="`${property.title}, immagine ${index + 1}`"
              :loading="index === 0 ? null : 'lazy'"
              :preload="index === 0"
              width="1024"
              height="576"
              fit="contain"
              format="webp"
              :modifiers="{ b: 'ffffff' }"
              sizes="sm:300px md:350px lg:1024px"
              aria-hidden="true"
            />
            <div class="swiper-lazy-preloader"></div>
          </div>
        </LazySwiper>
        <div v-else class="ucrs-gallery__overlay">
          <NuxtImg
            src="/assets/img/ucrs-placeholder.png"
            alt="UniCredit RE Services Placeholder"
            class="ucrs-gallery__item"
            loading="lazy"
            width="790"
            height="444"
          />
        </div>
      </ClientOnly>

      <div class="ucrs-gallery__geo">
        <SvgIcon :name="iconName" class="w-7 h-6 mr-2" />
        <p class="ucrs-gallery__geo-inner">
          <strong>{{ property.location.city }}</strong>
          <template v-if="property.location.locality.neighbourhood"
            ><span class="inline-block mx-2">|</span
            >{{ property.location.locality.neighbourhood }}</template
          >
        </p>
      </div>
      <div
        v-if="property.images && property.images.length"
        class="ucrs-gallery__counter"
      >
        <span v-text="currentSlide"></span>/<span
          v-text="property.images.length"
        ></span>
      </div>
      <div class="ucrs-gallery__icons space-x-4">
        <a
          v-if="property.features.virtual_tour && !media"
          :href="property.url"
          target="_blank"
        >
          <SvgIcon name="virtual-tour-new" class="w-17 h-17" />
        </a>
        <a
          v-if="property.features.url_video && !media"
          :href="property.url"
          target="_blank"
        >
          <SvgIcon name="video-new" class="w-17 h-17" />
        </a>
        <SvgIcon
          v-if="property.features.virtual_tour && media"
          tabindex="0"
          role="button"
          aria-label="Mostra virtual tour"
          name="virtual-tour-new"
          class="w-17 h-17 cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:rounded-full focus-visible:ring-blue"
          @keyup.enter="onVirtualTourIconClick"
          @click="onVirtualTourIconClick"
        />
        <SvgIcon
          v-if="property.features.url_video && media"
          tabindex="0"
          role="button"
          aria-label="Mostra video"
          name="video-new"
          class="w-17 h-17 cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:rounded-full focus-visible:ring-blue"
          @keyup.enter="onVideoIconClick"
          @click="onVideoIconClick"
        />
        <SvgIcon
          v-if="media"
          tabindex="0"
          name="gallery"
          role="button"
          aria-label="Mostra galleria immagini"
          class="w-17 h-17 cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:rounded-full focus-visible:ring-blue"
          @keyup.enter="onGalleryIconClick"
          @click="onGalleryIconClick"
        />
      </div>
    </div>
  </div>
</template>

<script>
// Libs
import { defineAsyncComponent } from 'vue'

// Components
import PropertyLightbox from '~/components/PropertyLightbox'
const LazyYoutubeVideo = () => import('vue-lazy-youtube-video')
const LazySwiper = defineAsyncComponent(() =>
  // TODO: test if lazyness is working and necessary
  import('~/components/Swiper.vue')
)

export default {
  components: {
    LazyYoutubeVideo,
    LazySwiper,
  },

  props: {
    property: {
      type: Object,
      default: () => {
        return null
      },
    },

    media: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      renderYouTube: false,
      currentSlide: 1,
      showVirtualTour: true,
      showVideo: false,
      swiperOptions: {},
      tealiumTracked: false,
    }
  },

  computed: {
    placeholder() {
      return this.$img('/assets/img/ucrs-placeholder.png', {
        width: 790,
        height: 443,
      })
    },

    swiperBackground() {
      const { placeholder } = this

      return `background-image: url(${placeholder})`
    },

    virtualTourSrc() {
      return this.property.features.virtual_tour
        ? `${this.property.features.virtual_tour.trim()}?viewMode=embed&hideLogo`
        : null
    },

    videoId() {
      if (!this.property.features.url_video) return null
      let id = null
      const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
      const match = this.property.features.url_video.match(regExp)
      if (match && match[2].length === 11) id = match[2]
      return id
    },

    iconName() {
      const name = this.property.building.is_luxury
        ? 'prestige'
        : this.property.building.category.name === 'Residenziale'
        ? 'house'
        : this.property.building.category.name === 'Commerciale'
        ? 'commercial'
        : this.property.building.category.name === 'Cantieri'
        ? 'enterprise'
        : this.property.building.category.name === 'Terreni'
        ? 'lands'
        : ''
      return name
    },
  },
  methods: {
    updateCurrentSlide(activeIndex) {
      this.currentSlide = activeIndex + 1
    },

    playVideo() {
      if (this.property.features.url_video) this.renderYouTube = true
    },

    pauseVideo() {
      if (this.property.features.url_video) this.renderYouTube = false
    },

    clickImage(index) {
      this.$oruga.modal.open({
        parent: this,
        component: PropertyLightbox,
        animation: 'ucrs-modal-',
        contentClass: 'ucrs-modal__container--center',
        width: 1230,
        props: {
          images: this.property.images,
          alt: `${this.property.title}, immagine`,
          startAt: index,
        },
        onCancel: this.handleLightboxClose,
        events: {
          close: () => {
            this.handleLightboxClose()
          },
        },
      })
      this.$tealium.track({
        event_name: 'apertura_gallery',
        id: this.property.id,
      })
    },

    handleLightboxClose() {
      this.$tealium.track({
        event_name: 'chiusura_gallery',
        id: this.property.id,
      })
    },

    onVirtualTourIconClick() {
      this.showVirtualTour = true
      this.showVideo = false
      this.pauseVideo()

      if (!this.tealiumTracked) {
        this.$tealium.track({ event_name: 'vtour_start', id: this.property.id })
        this.tealiumTracked = true
      }
    },

    onVideoIconClick() {
      this.showVirtualTour = false
      this.showVideo = true
      this.playVideo()
    },

    onGalleryIconClick() {
      this.showVideo = false
      this.showVirtualTour = false
      this.pauseVideo()
    },
  },
}
</script>

<style lang="postcss">
@import '~/assets/css/components/gallery';
</style>
